//src/pages/gro/QuestionnaireDetailPage.tsx
import { useMemo, useState } from "react";
import {
  ChevronLeft,
  Copy,
  Eye,
  FileText,
  LayoutGrid,
  MoreHorizontal,
  Plus,
  Rocket,
  RotateCcw,
  Rows3,
  Trash2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QuestionnaireVersionBuilder } from "@/components/gro/QuestionnaireVersionBuilder";
import { QuestionnaireVersionPreview } from "@/components/gro/QuestionnaireVersionPreview";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCloneQuestionnaireVersion,
  useCreateQuestionnaireOption,
  useCreateQuestionnaireQuestion,
  useCreateQuestionnaireSection,
  useCreateQuestionnaireVersion,
  useDeleteQuestionnaireOption,
  useDeleteQuestionnaireQuestion,
  useDeleteQuestionnaireSection,
  usePublishQuestionnaireVersion,
  useQuestionnaireDetail,
  useQuestionnaireVersionDetail,
  useQuestionnaireVersions,
  useUpdateQuestionnaireOption,
  useUpdateQuestionnaireQuestion,
  useUpdateQuestionnaireSection,
} from "@/hooks/useQuestionnaires";
import {
  useDeleteQuestionnaire,
  useDeleteQuestionnaireVersion,
  useUnpublishQuestionnaireVersion,
} from "@/hooks/useQuestionnaireLifecycle";
import { useDimensions } from "@/hooks/useDimensions";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type {
  QuestionnaireCloneVersionIn,
  QuestionnaireOptionUpsertIn,
  QuestionnaireQuestionUpsertIn,
  QuestionnaireSectionUpsertIn,
  QuestionnaireVersionCreateIn,
} from "@/types/gro";

const VERSION_STATUS_LABEL: Record<string, string> = {
  draft: "Rascunho",
  published: "Publicado",
  archived: "Arquivado",
};

type ConfirmAction =
  | "unpublish"
  | "deleteVersion"
  | "deleteQuestionnaire"
  | null;

type DetailTab = "questions" | "sections" | "preview";

function getVersionLabel(versionNumber: number, versionLabel?: string | null) {
  return versionLabel?.trim() ? versionLabel : `v${versionNumber}`;
}

function getVersionStatusLabel(status?: string | null) {
  return VERSION_STATUS_LABEL[status || ""] ?? status ?? "-";
}

const DETAIL_TABS: Array<{
  key: DetailTab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { key: "questions", label: "Perguntas", icon: LayoutGrid },
  { key: "sections", label: "Seções", icon: Rows3 },
  { key: "preview", label: "Visualizar", icon: Eye },
];

export default function QuestionnaireDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();

  const canWrite =
    user?.permissions?.includes(PERMISSIONS.QUESTIONNAIRES_WRITE) ?? false;

  const detailQ = useQuestionnaireDetail(id);
  const versionsQ = useQuestionnaireVersions(id);
  const dimensionsQ = useDimensions({ is_active: true });

  const versions = versionsQ.data ?? [];
  const availableDimensions = dimensionsQ.data ?? [];

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [activeTab, setActiveTab] = useState<DetailTab>("questions");

  const currentVersionId = useMemo(() => {
    if (selectedVersionId) return selectedVersionId;
    return versions[0]?.id ?? null;
  }, [selectedVersionId, versions]);

  const versionDetailQ = useQuestionnaireVersionDetail(
    currentVersionId || undefined,
  );

  const createVersionMut = useCreateQuestionnaireVersion();
  const publishMut = usePublishQuestionnaireVersion();
  const cloneMut = useCloneQuestionnaireVersion();
  const deleteQuestionnaireMut = useDeleteQuestionnaire();
  const deleteVersionMut = useDeleteQuestionnaireVersion();
  const unpublishVersionMut = useUnpublishQuestionnaireVersion();

  const createSectionMut = useCreateQuestionnaireSection();
  const updateSectionMut = useUpdateQuestionnaireSection();
  const deleteSectionMut = useDeleteQuestionnaireSection();

  const createQuestionMut = useCreateQuestionnaireQuestion();
  const updateQuestionMut = useUpdateQuestionnaireQuestion();
  const deleteQuestionMut = useDeleteQuestionnaireQuestion();

  const createOptionMut = useCreateQuestionnaireOption();
  const updateOptionMut = useUpdateQuestionnaireOption();
  const deleteOptionMut = useDeleteQuestionnaireOption();

  async function handleNewVersion() {
    if (!id) return;

    const body: QuestionnaireVersionCreateIn = {
      version_label: "",
      instructions: "",
    };

    try {
      const created = await createVersionMut.mutateAsync({
        questionnaireId: id,
        body,
      });
      setSelectedVersionId(created.id);
      toast.success("Nova versão criada com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao criar versão.");
    }
  }

  async function handleCloneVersion() {
    if (!id || !currentVersionId) return;

    const body: QuestionnaireCloneVersionIn = {
      version_label: "",
      instructions: versionDetailQ.data?.instructions ?? "",
    };

    try {
      const created = await cloneMut.mutateAsync({
        questionnaireId: id,
        versionId: currentVersionId,
        body,
      });
      setSelectedVersionId(created.id);
      toast.success("Versão clonada com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao clonar versão.");
    }
  }

  async function handlePublishVersion() {
    if (!id || !currentVersionId) return;

    try {
      await publishMut.mutateAsync({
        questionnaireId: id,
        versionId: currentVersionId,
      });
      toast.success("Versão publicada com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao publicar versão.");
    }
  }

  async function handleConfirmAction() {
    if (!id) return;

    try {
      if (confirmAction === "unpublish" && currentVersionId) {
        await unpublishVersionMut.mutateAsync({
          questionnaireId: id,
          versionId: currentVersionId,
        });
        toast.success("Versão despublicada com sucesso.");
      }

      if (confirmAction === "deleteVersion" && currentVersionId) {
        const deletingLastVersion = versions.length <= 1;

        await deleteVersionMut.mutateAsync({
          questionnaireId: id,
          versionId: currentVersionId,
        });

        if (deletingLastVersion) {
          toast.success("Questionário excluído com sucesso.");
          navigate("/gro/questionnaires");
          return;
        }

        setSelectedVersionId(null);
        toast.success("Versão excluída com sucesso.");
      }

      if (confirmAction === "deleteQuestionnaire") {
        await deleteQuestionnaireMut.mutateAsync({ questionnaireId: id });
        toast.success("Questionário excluído com sucesso.");
        navigate("/gro/questionnaires");
        return;
      }

      setConfirmAction(null);
    } catch (e: any) {
      toast.error(e?.message || "Não foi possível concluir a operação.");
    }
  }

  const questionnaire = detailQ.data;
  const version = versionDetailQ.data;
  const hasPublishedVersion = versions.some(
    (item) => item.status === "published",
  );

  const canDeleteQuestionnaire =
    canWrite && versions.length > 0 && !hasPublishedVersion;

  const canDeleteVersion =
    canWrite && !!currentVersionId && version?.status === "draft";

  const canUnpublishVersion =
    canWrite && !!currentVersionId && version?.status === "published";

  const confirmTitle =
    confirmAction === "unpublish"
      ? "Despublicar versão"
      : confirmAction === "deleteVersion"
        ? "Excluir versão"
        : confirmAction === "deleteQuestionnaire"
          ? "Excluir questionário"
          : "";

  const confirmDescription =
    confirmAction === "unpublish"
      ? "Esta versão voltará para rascunho. Se ela já estiver vinculada a campanhas, a operação será bloqueada."
      : confirmAction === "deleteVersion"
        ? versions.length <= 1
          ? "Esta é a única versão em rascunho. Ao excluí-la, o questionário inteiro também será excluído."
          : "Deseja excluir esta versão em rascunho?"
        : confirmAction === "deleteQuestionnaire"
          ? versions.length > 1
            ? `Este questionário possui ${versions.length} versões em rascunho. Todas elas serão excluídas.`
            : "Deseja excluir este questionário em rascunho?"
          : "";

  const confirmButtonLabel =
    confirmAction === "unpublish"
      ? "Despublicar"
      : confirmAction === "deleteVersion"
        ? versions.length <= 1
          ? "Excluir tudo"
          : "Excluir versão"
        : confirmAction === "deleteQuestionnaire"
          ? "Excluir questionário"
          : "Confirmar";

  const confirmLoading =
    unpublishVersionMut.isPending ||
    deleteVersionMut.isPending ||
    deleteQuestionnaireMut.isPending;

  return (
    <div>
      <PageHeader
        title={questionnaire?.name || "Questionário"}
        description={
          questionnaire?.description || "Monte versões, seções, perguntas e opções."
        }
      >
        <div className="flex shrink-0 gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/gro/questionnaires")}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <MoreHorizontal className="mr-2 h-4 w-4" />
                Mais opções
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                disabled={!canWrite}
                onSelect={() => navigate(`/gro/questionnaires/${id}/editar`)}
              >
                <FileText className="mr-2 h-4 w-4" />
                Editar cabeçalho
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canWrite} onSelect={handleNewVersion}>
                <Plus className="mr-2 h-4 w-4" />
                Nova versão
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canWrite || !currentVersionId} onSelect={handleCloneVersion}>
                <Copy className="mr-2 h-4 w-4" />
                Clonar versão
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!canWrite || !currentVersionId || version?.status !== "draft"}
                onSelect={handlePublishVersion}
              >
                <Rocket className="mr-2 h-4 w-4" />
                Publicar versão
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canUnpublishVersion} onSelect={() => setConfirmAction("unpublish")}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Despublicar versão
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled={!canDeleteVersion} onSelect={() => setConfirmAction("deleteVersion")}>
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir versão
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!canDeleteQuestionnaire}
                onSelect={() => setConfirmAction("deleteQuestionnaire")}
                title={
                  canDeleteQuestionnaire
                    ? "Excluir questionário"
                    : "A exclusão do questionário só é permitida quando todas as versões estão em rascunho"
                }
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir questionário
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </PageHeader>

      <div className="mb-4 grid gap-4 md:grid-cols-[1.2fr_2fr]">
        <Card className="p-4">
          <div className="mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <h3 className="font-semibold">Versões</h3>
          </div>

          <div className="space-y-2">
            {versions.map((item) => {
              const selected = item.id === currentVersionId;
              const label = getVersionLabel(
                item.version_number,
                item.version_label,
              );

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedVersionId(item.id)}
                  className={`w-full rounded-lg border p-3 text-left transition ${
                    selected ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{label}</div>
                    <Badge
                      variant={
                        item.status === "published"
                          ? "default"
                          : item.status === "draft"
                            ? "secondary"
                            : "outline"
                      }
                    >
                      {getVersionStatusLabel(item.status)}
                    </Badge>
                  </div>

                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">Questionário</div>
              <div className="font-semibold">
                {questionnaire?.code} — {questionnaire?.name}
              </div>
            </div>

            <Badge variant={questionnaire?.is_active ? "default" : "outline"}>
              {questionnaire?.is_active ? "Ativo" : "Inativo"}
            </Badge>
          </div>

          {version ? (
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Versão:</span>{" "}
                {getVersionLabel(version.version_number, version.version_label)}
              </div>

              <div>
                <span className="font-medium">Status:</span>{" "}
                {getVersionStatusLabel(version.status)}
              </div>


              {version.instructions ? (
                <div>
                  <span className="font-medium">Instruções:</span>{" "}
                  {version.instructions}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              Selecione uma versão para visualizar os detalhes.
            </div>
          )}
        </Card>
      </div>

      {version ? (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {DETAIL_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.key;

              return (
                <Button
                  key={tab.key}
                  type="button"
                  variant={active ? "default" : "outline"}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {tab.label}
                </Button>
              );
            })}
          </div>

          {activeTab === "preview" ? (
            <QuestionnaireVersionPreview version={version} />
          ) : (
            <QuestionnaireVersionBuilder
              version={version}
              availableDimensions={availableDimensions}
              canWrite={canWrite}
              mode={activeTab === "sections" ? "sections" : "questions"}
              onCreateSection={async (body: QuestionnaireSectionUpsertIn) => {
                if (!currentVersionId) return;
                await createSectionMut.mutateAsync({
                  versionId: currentVersionId,
                  body,
                });
              }}
              onUpdateSection={async (
                sectionId: string,
                body: QuestionnaireSectionUpsertIn,
              ) => {
                if (!currentVersionId) return;
                await updateSectionMut.mutateAsync({
                  versionId: currentVersionId,
                  sectionId,
                  body,
                });
              }}
              onDeleteSection={async (sectionId: string) => {
                if (!currentVersionId) return;
                await deleteSectionMut.mutateAsync({
                  versionId: currentVersionId,
                  sectionId,
                });
              }}
              onCreateQuestion={async (body: QuestionnaireQuestionUpsertIn) => {
                if (!currentVersionId) return;
                return await createQuestionMut.mutateAsync({
                  versionId: currentVersionId,
                  body,
                });
              }}
              onUpdateQuestion={async (
                questionId: string,
                body: QuestionnaireQuestionUpsertIn,
              ) => {
                if (!currentVersionId) return;
                await updateQuestionMut.mutateAsync({
                  versionId: currentVersionId,
                  questionId,
                  body,
                });
              }}
              onDeleteQuestion={async (questionId: string) => {
                if (!currentVersionId) return;
                await deleteQuestionMut.mutateAsync({
                  versionId: currentVersionId,
                  questionId,
                });
              }}
              onCreateOption={async (
                questionId: string,
                body: QuestionnaireOptionUpsertIn,
              ) => {
                if (!currentVersionId) return;
                await createOptionMut.mutateAsync({
                  versionId: currentVersionId,
                  questionId,
                  body,
                });
              }}
              onUpdateOption={async (
                optionId: string,
                _questionId: string,
                body: QuestionnaireOptionUpsertIn,
              ) => {
                if (!currentVersionId) return;
                await updateOptionMut.mutateAsync({
                  versionId: currentVersionId,
                  optionId,
                  body,
                });
              }}
              onDeleteOption={async (
                optionId: string,
                _questionId: string,
              ) => {
                if (!currentVersionId) return;
                await deleteOptionMut.mutateAsync({
                  versionId: currentVersionId,
                  optionId,
                });
              }}
            />
          )}
        </>
      ) : (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Nenhuma versão encontrada para este questionário.
        </Card>
      )}

      <Dialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmTitle}</DialogTitle>
            <DialogDescription>{confirmDescription}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              disabled={confirmLoading}
            >
              Cancelar
            </Button>

            <Button onClick={handleConfirmAction} disabled={confirmLoading}>
              {confirmLoading ? "Processando..." : confirmButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
